import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import {
  getRevenueBreakdown,
  getRevenueCurrent,
  getRevenueProjection,
  getRevenueStatus,
  refreshRevenueSnapshot,
  listFlagCodes,
} from "../../api/system/system.api";

// 매출 대시보드(A2 Phase D) — sysadmin 전용. 설계: design_v5.md §C/§D.
// 금액은 전부 VAT 전 공급가(net). 순매출 = 매출 − 원가. (청구서 현황 금액만 VAT 포함 총액)
// 가입 위저드 선결제(install_payment)는 서버가 결제월 기준으로 설치비/스티커 시리즈에 더해 준다(2026-10-16) —
// 여기서는 그 분리값(prepaid)을 표·KPI 에 '포함' 으로 표기만 한다.

const won = (n) => (n ?? 0).toLocaleString("ko-KR");

// 카테고리(시리즈) 표시 순서·라벨·색. ONE_TIME 은 서버에서 INSTALL/STICKER/ETC 로 세분됨.
const SERIES_DEFS = [
  { key: "MONTHLY", label: "서비스 이용료", color: "#2563eb" },
  { key: "MESSAGE", label: "메시지", color: "#16a34a" },
  { key: "ACQUISITION", label: "고객 획득", color: "#f59e0b" },
  { key: "MOMENT", label: "모먼트", color: "#a855f7" },
  { key: "GIFT", label: "선물하기", color: "#ec4899" },
  { key: "INSTALL", label: "설치비", color: "#0891b2" },
  { key: "STICKER", label: "스티커", color: "#14b8a6" },
  { key: "ETC", label: "기타", color: "#6b7280" },
];
const labelOf = (k) => SERIES_DEFS.find((s) => s.key === k)?.label ?? k;
const colorOf = (k) => SERIES_DEFS.find((s) => s.key === k)?.color ?? "#9ca3af";

// 브라우저 기준 월 계산(YYYY-MM).
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;


// 스냅샷 기준시각(ISO) → 로컬 표시. 없으면 '-'.
const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("ko-KR") : "-");

const SOURCE_LABEL = {
  NEW_CONFIRMED: "확정",
  NEW_LIVE: "라이브",
  MIXED: "혼합",
  PRECUTOFF: "교차검증(구청구)",
  LEGACY_ONLY: "legacy(구)",
};

export default function RevenueDashboard() {
  const now = useMemo(() => new Date(), []);
  const thisMonth = ym(now);
  const nextMonth = ym(new Date(now.getFullYear(), now.getMonth() + 1, 1));
  const defaultFrom = ym(new Date(now.getFullYear(), now.getMonth() - 5, 1));

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(thisMonth);
  const [net, setNet] = useState(false);
  const [flag, setFlag] = useState("");
  const [flagCodes, setFlagCodes] = useState([]);

  const [breakdown, setBreakdown] = useState(null);
  const [current, setCurrent] = useState(null);
  const [projection, setProjection] = useState(null);
  const [status, setStatus] = useState(null);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [queried, setQueried] = useState(false); // 조회 버튼을 눌러야 집계 실행(DB 부하 방지)

  const run = async (fn) => {
    setLoading(true);
    setMsg("");
    try {
      await fn();
    } catch (e) {
      setMsg(`오류: ${e?.response?.data?.data || e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const loadFlags = () =>
    run(async () => {
      const resp = await listFlagCodes();
      setFlagCodes(resp?.data ?? []);
    });

  // 집계 4종 조회. 반드시 사용자가 "조회"를 눌렀을 때만 실행(탭 진입만으로는 실행하지 않음).
  // 기본은 야간 스냅샷 조회(경량). refresh=true 면 서버가 조회 전에 해당 월 스냅샷을 재계산(실시간).
  const reload = (refresh = false) =>
    run(async () => {
      const [b, c, p, s] = await Promise.all([
        getRevenueBreakdown(from, to, { flag, net, refresh }),
        getRevenueCurrent(thisMonth, flag, refresh),
        getRevenueProjection(nextMonth, flag, refresh),
        getRevenueStatus(thisMonth, flag, refresh),
      ]);
      setBreakdown(b?.data ?? null);
      setCurrent(c?.data ?? null);
      setProjection(p?.data ?? null);
      setStatus(s?.data ?? null);
      setQueried(true);
    });

  // 스냅샷 재계산 — **대상 월을 관리자가 지정**한다.
  //
  // 야간 배치는 '어제가 속한 달' 하나만 돌기 때문에(= 전날까지의 가정산), 그 외의 달이나 오늘자 값을
  // 지금 당장 봐야 할 때 이 버튼을 쓴다. 종전에는 조회 범위의 모든 월을 한꺼번에 재계산했는데,
  // 과거 달까지 매번 다시 계산하면 운영 DB 에 불필요한 부하를 주고 확정 전 값이 계속 흔들린다.
  // 확정된 청구서가 있는 채널은 서버가 알아서 건너뛴다(확정분은 정산서가 권위 소스).
  const [snapshotMonth, setSnapshotMonth] = useState(thisMonth);

  const refreshOne = () =>
    run(async () => {
      const rows = await refreshRevenueSnapshot(snapshotMonth);
      const [b, c, p, s] = await Promise.all([
        getRevenueBreakdown(from, to, { flag, net }),
        getRevenueCurrent(thisMonth, flag),
        getRevenueProjection(nextMonth, flag),
        getRevenueStatus(thisMonth, flag),
      ]);
      setBreakdown(b?.data ?? null);
      setCurrent(c?.data ?? null);
      setProjection(p?.data ?? null);
      setStatus(s?.data ?? null);
      setQueried(true);
      setMsg(`${snapshotMonth} 스냅샷 재계산 완료(미확정 채널 ${rows?.data ?? 0}행)`);
    });

  // 마운트 시에는 flag 후보(가벼운 단일 쿼리)만 로드. 집계는 조회 버튼으로만.
  useEffect(() => {
    loadFlags();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = net ? "net" : "gross";

  // 차트 시리즈: 월범위 전체에서 등장한 카테고리 키의 합집합(정의된 순서 우선).
  const { chartSeries, chartColors, monthLabels } = useMemo(() => {
    const months = breakdown?.months ?? [];
    const labels = months.map((m) => m.month);
    const seen = new Set();
    months.forEach((m) => Object.keys(m.byCategory || {}).forEach((k) => seen.add(k)));
    const ordered = [
      ...SERIES_DEFS.map((s) => s.key).filter((k) => seen.has(k)),
      ...[...seen].filter((k) => !SERIES_DEFS.some((s) => s.key === k)),
    ];
    return {
      monthLabels: labels,
      chartColors: ordered.map(colorOf),
      chartSeries: ordered.map((k) => ({
        name: labelOf(k),
        data: months.map((m) => m.byCategory?.[k]?.[pick] ?? 0),
      })),
    };
  }, [breakdown, pick]);

  const chartOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { columnWidth: "55%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: monthLabels },
    yaxis: { labels: { formatter: (v) => won(Math.round(v)) } },
    colors: chartColors,
    legend: { position: "top" },
    tooltip: { y: { formatter: (v) => `${won(Math.round(v))}원` } },
    grid: { borderColor: "#eee" },
  };

  const confirmRate =
    status && status.activeChannels > 0
      ? Math.round((status.confirmedChannels / status.activeChannels) * 100)
      : 0;

  const kpi = (title, value, sub) => (
    <div className="border rounded-lg p-4 bg-white flex-1 min-w-[180px]">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-2xl font-bold mt-1">{won(value)}<span className="text-sm font-normal text-gray-400"> 원</span></div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="text-lg font-semibold">매출 대시보드</h2>
        <button className="px-2 py-1 text-sm border rounded" onClick={() => reload(false)} disabled={loading}>
          조회(스냅샷)
        </button>
        <span className="flex items-center gap-1 text-sm">
          <input
            className="border rounded px-2 py-1 text-sm w-24"
            value={snapshotMonth}
            onChange={(e) => setSnapshotMonth(e.target.value)}
            placeholder="2026-07"
            title="재계산할 대상 월(YYYY-MM)"
          />
          <button
            className="px-2 py-1 text-sm border rounded bg-emerald-600 text-white disabled:opacity-50"
            onClick={refreshOne}
            disabled={loading}
            title="지정한 월의 가정산 스냅샷을 지금 다시 계산합니다. 확정된 청구서가 있는 채널은 건너뜁니다."
          >
            스냅샷 재계산
          </button>
        </span>
        {loading && <span className="text-sm text-gray-500">처리 중…</span>}
        {msg && (
          <span className={`text-sm ${msg.startsWith("오류") ? "text-red-600" : "text-emerald-700"}`}>{msg}</span>
        )}
      </div>

      {/* 스냅샷 기준시각·최신여부 안내 */}
      {queried && (
        <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
          <span className="text-gray-500">
            당월 가정산 기준시각: <b>{fmtAt(current?.snapshotAt)}</b>
          </span>
          {current?.confirmedChannels + current?.unconfirmedChannels > 0 && (
            <span className="text-gray-500">
              · 확정 <b>{current?.confirmedChannels ?? 0}</b> / 가정산 <b>{current?.unconfirmedChannels ?? 0}</b> 채널
            </span>
          )}
          {current?.stale && (
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
              어제자 가정산 미반영 — “스냅샷 재계산” 권장
            </span>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500 mb-4">
        금액은 <b>VAT 전 공급가(net)</b> 기준. <b>순매출</b> = 매출 − 원가(정가표 cost_net).
        <b> 확정된 청구서가 있는 채널은 그 청구서 금액</b>을, 나머지는 <b>가정산 스냅샷</b>을 씁니다.
        가입 위저드 <b>선결제</b>(설치비·스티커)는 정산서에 없으므로 <b>결제월 기준</b>으로 설치비/스티커 시리즈에 따로 더합니다(표의 '선결제' 열).
        가정산 스냅샷은 매일 새벽 <b>어제까지의 사용량</b>으로 갱신되며(매월 1일 배치는 전월 말일까지를 기록),
        그래서 <b>당일자 값은 “스냅샷 재계산”을 눌러야</b> 보입니다. 다음달 예상 매출은 계약 기준 실시간 계산이라 항상 최신입니다.
        청구 cutoff({breakdown?.cutoffMonth || "-"}) <b>이전 월</b>은 실제 청구가 구 빌링이며 여기 값은 <b>교차검증용 참고값</b>입니다.
      </p>

      {/* KPI */}
      {queried && (
        <div className="flex gap-3 mb-5 flex-wrap">
          {kpi(
            `당월 실시간 매출 (${thisMonth})`,
            current?.gross,
            `순매출 ${won(current?.net)} · 채널 ${current?.channelCount ?? 0}개` +
              (current?.prepaidCount > 0 ? ` · 선결제 ${won(current?.prepaid)} (${current.prepaidCount}건) 포함` : ""),
          )}
          {kpi(`당월 순매출 (${thisMonth})`, current?.net, `원가 ${won(current?.cost)}`)}
          {kpi(`다음달 예상 (${nextMonth})`, projection?.total, `정기 ${won(projection?.recurring)} + 일회성 ${won(projection?.oneTime)}`)}
        </div>
      )}

      {/* 청구서 현황 */}
      {queried && status && (
        <div className="border rounded-lg p-4 bg-white mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">청구서 현황 · {status.month}</div>
            <div className="text-sm text-gray-500">
              확정 {status.confirmedChannels} / {status.activeChannels} 채널 ({confirmRate}%)
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded h-3 mb-3 overflow-hidden">
            <div className="bg-blue-600 h-3" style={{ width: `${confirmRate}%` }} />
          </div>
          <div className="flex gap-6 text-sm flex-wrap">
            <div>
              <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-1" />
              확정(BILLED): <b>{won(status.confirmedTotal)}</b> 원
            </div>
            <div>
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />
              미확정(PENDING): <b>{won(status.pendingTotal)}</b> 원 · {status.pendingChannels}채널
            </div>
            <div className="text-gray-400">* 청구 총액(VAT 포함)</div>
          </div>
        </div>
      )}

      {/* 컨트롤 */}
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">시작월</div>
          <input type="month" className="border rounded px-2 py-1 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">종료월</div>
          <input type="month" className="border rounded px-2 py-1 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">플래그 필터</div>
          <select className="border rounded px-2 py-1 text-sm" value={flag} onChange={(e) => setFlag(e.target.value)}>
            <option value="">전체</option>
            {flagCodes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-sm select-none">
          <input type="checkbox" checked={net} onChange={(e) => setNet(e.target.checked)} />
          순매출로 보기
        </label>
        <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded" onClick={() => reload(false)} disabled={loading}>조회</button>
      </div>

      {/* 스택 차트 */}
      <div className="border rounded-lg p-4 bg-white">
        <div className="text-sm font-semibold mb-2">
          월별 {net ? "순매출" : "매출"} (카테고리 스택)
        </div>
        {!queried ? (
          <div className="text-sm text-gray-400 py-10 text-center">
            조회 조건을 설정한 뒤 <b>조회</b> 버튼을 눌러 데이터를 불러오세요.
            <div className="text-xs text-gray-300 mt-1">(집계는 DB 부하가 있어 자동 실행되지 않습니다.)</div>
          </div>
        ) : monthLabels.length > 0 ? (
          <Chart options={chartOptions} series={chartSeries} type="bar" height={360} />
        ) : (
          <div className="text-sm text-gray-400 py-10 text-center">데이터가 없습니다.</div>
        )}
        {breakdown?.months?.length > 0 && (
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full text-xs border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border px-2 py-1 text-left">월</th>
                  <th className="border px-2 py-1 text-right">매출(net)</th>
                  <th className="border px-2 py-1 text-right">원가</th>
                  <th className="border px-2 py-1 text-right">순매출</th>
                  <th className="border px-2 py-1 text-right" title="가입 위저드 토스 선결제(설치비·스티커) 공급가. 매출(net)에 이미 포함된 값의 분리 표시.">선결제</th>
                  <th className="border px-2 py-1 text-center">확정</th>
                  <th className="border px-2 py-1 text-center">출처</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.months.map((m) => (
                  <tr key={m.month}>
                    <td className="border px-2 py-1">{m.month}</td>
                    <td className="border px-2 py-1 text-right">{won(m.gross)}</td>
                    <td className="border px-2 py-1 text-right text-gray-500">{won(m.cost)}</td>
                    <td className="border px-2 py-1 text-right font-semibold">{won(m.net)}</td>
                    <td className="border px-2 py-1 text-right text-gray-600">
                      {m.prepaidCount > 0 ? `${won(m.prepaid)} (${m.prepaidCount}건)` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      {m.source === "LEGACY_ONLY" ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <>
                          <span className="text-gray-600">
                            {m.confirmedChannels}/{m.confirmedChannels + m.unconfirmedChannels}
                          </span>
                          {m.unconfirmedChannels > 0 && m.frozen && (
                            <span
                              className="ml-1 px-1 rounded bg-amber-100 text-amber-700"
                              title="야간 배치가 더 이상 건드리지 않는 지난 달입니다. 미확정 채널 값은 마지막 가정산 스냅샷에서 고정돼 있으며, '스냅샷 재계산'으로 그 달을 지정해야 다시 계산됩니다."
                            >
                              미확정 고정
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="border px-2 py-1 text-center text-gray-500">{SOURCE_LABEL[m.source] ?? m.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
